import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'nexuscms_cookie_consent';

const CookieBanner = ({ settings = {} }) => {
  const [visible, setVisible] = useState(false);

  const {
    enabled = false,
    message = 'We use cookies to enhance your browsing experience and analyse site traffic. By clicking "Accept", you consent to the use of cookies.',
    accept_text = 'Accept',
    decline_text = 'Decline',
    privacy_url = '',
    privacy_text = 'Privacy Policy',
    position = 'bottom',
    bg_color = '#1e293b',
    text_color = '#f8fafc',
    button_bg = '#4f46e5',
    button_text_color = '#ffffff',
    show_decline = true,
  } = settings;

  useEffect(() => {
    if (!enabled) return;
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [enabled]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ consent: 'accepted', date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ consent: 'declined', date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleClose = () => {
    setVisible(false);
  };

  if (!enabled || !visible) return null;

  const isTop = position === 'top';

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed left-0 right-0 z-[9999] px-4 py-0"
      style={{
        [isTop ? 'top' : 'bottom']: 0,
        animation: `cookieBannerSlide 0.4s ease-out`,
      }}
    >
      <style>{`
        @keyframes cookieBannerSlide {
          from { 
            opacity: 0;
            transform: translateY(${isTop ? '-100%' : '100%'});
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div
        className="max-w-6xl mx-auto rounded-t-xl shadow-2xl"
        style={{
          backgroundColor: bg_color,
          color: text_color,
          borderTop: isTop ? 'none' : `3px solid ${button_bg}`,
          borderBottom: isTop ? `3px solid ${button_bg}` : 'none',
        }}
      >
        <div className="relative px-6 py-4">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="cookie-close-btn"
          >
            <X size={16} style={{ color: text_color, opacity: 0.6 }} />
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pr-8">
            <div className="flex-1 text-sm leading-relaxed" style={{ color: text_color, opacity: 0.9 }}>
              {message}
              {privacy_url && (
                <>
                  {' '}
                  <a
                    href={privacy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline font-medium"
                    style={{ color: text_color }}
                    data-testid="cookie-privacy-link"
                  >
                    {privacy_text}
                  </a>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {show_decline && (
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-white/10"
                  style={{
                    color: text_color,
                    borderColor: `${text_color}40`,
                  }}
                  data-testid="cookie-decline-btn"
                >
                  {decline_text}
                </button>
              )}
              <button
                onClick={handleAccept}
                className="px-5 py-2 text-sm font-semibold rounded-lg transition-colors hover:opacity-90"
                style={{
                  backgroundColor: button_bg,
                  color: button_text_color,
                }}
                data-testid="cookie-accept-btn"
              >
                {accept_text}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
